// Pure helpers for the catalogue images screen: field checks (through CheckValid) and the bodies
// the OMS takes. Nothing here calls the network or reads React state.
import CheckValid from "../../Common/CheckValid";
import { formatDateTime } from "../common/format";

// The markets the screen offers. India is where the warranty programme runs first.
export const CATALOGUE_COUNTRIES = [
  { code: "IN", label: "India" },
  { code: "ES", label: "Spain" },
  { code: "GB", label: "United Kingdom" },
];
export const DEFAULT_CATALOGUE_COUNTRY = "IN";

// On since the OMS forwards the model image routes (em-biz-backend a772eaaf). Set false to hide the
// tab again if the OMS in front of this build answers 404 on them.
export const WARRANTY_MODEL_IMAGES_ENABLED = true;

const ALL_TABS = [
  { key: "components", label: "Component icons" },
  { key: "models", label: "Model images" },
];

// The tabs the screen shows; a link to a hidden tab opens the first one.
export const catalogueTabs = (modelImagesEnabled = WARRANTY_MODEL_IMAGES_ENABLED) =>
  ALL_TABS.filter((tab) => tab.key !== "models" || modelImagesEnabled);

export const URL_MAX = 2048;
export const LABEL_MAX = 60;
export const PRODUCT_GROUP_MAX = 120;

const ask = (value, states) => {
  let message = "";
  CheckValid(value, {
    ...states,
    error: (m) => {
      message = m;
    },
  });
  return message;
};

// "" when the address may be sent: blank (no image) or an https address.
export const imageUrlError = (value, label = "Image URL") =>
  ask(typeof value === "string" ? value : "", { type: "warranty_image_url", label, max: URL_MAX });

export const productIdError = (value) =>
  ask(typeof value === "string" ? value : "", { type: "warranty_optional_uuid", label: "Product id" });

// A blank text field is sent as null.
export const blankToNull = (value) => {
  const text = typeof value === "string" ? value.trim() : "";
  return text === "" ? null : text;
};

// The editable copy of one component row: text fields as strings, as the inputs hold them.
export const componentDraft = (item) => ({
  label: typeof item?.label === "string" ? item.label : "",
  iconUrl: typeof item?.iconUrl === "string" ? item.iconUrl : "",
  sortOrder: Number.isInteger(item?.sortOrder) ? String(item.sortOrder) : "",
  active: item?.active !== false,
});

// Field messages for a component draft; an empty object means it may be saved.
export const componentErrors = (draft) => {
  const errors = {};
  const label = typeof draft?.label === "string" ? draft.label.trim() : "";
  if (label === "") {
    errors.label = "Label cannot be empty";
  } else if (label.length > LABEL_MAX) {
    errors.label = `Label can have at most ${LABEL_MAX} characters`;
  }
  const url = imageUrlError(draft?.iconUrl, "Icon URL");
  if (url) {
    errors.iconUrl = url;
  }
  const sort = typeof draft?.sortOrder === "string" ? draft.sortOrder.trim() : "";
  if (!/^\d{1,4}$/.test(sort)) {
    errors.sortOrder = "Sort order must be a whole number from 0 to 9999";
  }
  return errors;
};

export const componentPayload = (country, component, draft) => ({
  country,
  component,
  label: draft.label.trim(),
  iconUrl: blankToNull(draft.iconUrl),
  sortOrder: Number.parseInt(draft.sortOrder.trim(), 10),
  active: draft.active === true,
});

// True when the draft differs from what the server holds, so Save has something to send.
export const componentChanged = (item, draft) => {
  const saved = componentDraft(item);
  return (
    saved.label.trim() !== draft.label.trim() ||
    saved.iconUrl.trim() !== draft.iconUrl.trim() ||
    saved.sortOrder.trim() !== draft.sortOrder.trim() ||
    saved.active !== draft.active
  );
};

export const EMPTY_MODEL_FORM = { productGroup: "", productId: "", imageUrl: "", active: true };

// A list row into the form, for "Edit".
export const modelFormFrom = (item) => ({
  productGroup: typeof item?.productGroup === "string" ? item.productGroup : "",
  productId: typeof item?.productId === "string" ? item.productId : "",
  imageUrl: typeof item?.imageUrl === "string" ? item.imageUrl : "",
  active: item?.active !== false,
});

export const modelFormErrors = (form) => {
  const errors = {};
  const group = typeof form?.productGroup === "string" ? form.productGroup.trim() : "";
  if (group === "") {
    errors.productGroup = "Product group cannot be empty";
  } else if (group.length > PRODUCT_GROUP_MAX) {
    errors.productGroup = `Product group can have at most ${PRODUCT_GROUP_MAX} characters`;
  }
  const id = productIdError(form?.productId);
  if (id) {
    errors.productId = id;
  }
  const url = imageUrlError(form?.imageUrl, "Image URL");
  if (url) {
    errors.imageUrl = url;
  }
  return errors;
};

export const modelImagePayload = (country, form) => {
  const productId = blankToNull(form.productId);
  return {
    country,
    productGroup: form.productGroup.trim(),
    productId: productId === null ? null : productId.toLowerCase(),
    imageUrl: blankToNull(form.imageUrl),
    active: form.active === true,
  };
};

// "7 Oct 2026, 3:05 pm, staff #42" for the Updated column; the OMS sends the staff user id.
export const updatedText = (item) => {
  if (typeof item?.updatedAt !== "string" || item.updatedAt === "") {
    return "Never changed";
  }
  const when = formatDateTime(item.updatedAt);
  return item.updatedBy ? `${when}, staff #${item.updatedBy}` : when;
};
